/**
 * Exact dashboard selection scope.
 *
 * Dashboard cross-filtering has TWO channels, and conflating them is what made a KPI report 5
 * while the chart bucket it was filtered by said 3:
 *
 *   - SAME physical table  → raw WHERE predicates replayed on the target widget's own query.
 *   - DIFFERENT physical tables → one tag filter session per table, carrying ONLY the dimensions
 *     that belong to other tables.
 *
 * A tag session is a set of TagIds. Many rows can share one Master Tag, so intersecting two
 * same-table dimensions through tags widens the result to "every row under a matching tag"
 * instead of "the rows that satisfy both dimensions". Raw predicates are exact for that case;
 * tag sessions remain the only way to propagate across sources. See
 * `.codex/features/spatial-focus/README.md` → "Exact dashboard selection contract".
 *
 * Every function here is pure: it consumes the existing `chartSelections` entry shape and returns
 * new values. No Vuex, no api, no component state, no mutation of its inputs. Keep it
 * dependency-free — it is the shared vocabulary for the grid, the chart, and spatial focus.
 */

/** Physical table names are case-insensitive identity. `sourceKey` is NOT physical identity — it
 *  folds in saved-filter hashes, so two widgets on one table can carry different source keys. */
export function normalizeTableName(tableName) {
    const raw = tableName == null ? '' : String(tableName).trim();
    return raw ? raw.toLowerCase() : null;
}

/**
 * One WHERE-predicate per selected dimension over its selected values (values OR within a
 * dimension; dimensions AND across the array = the cross-filter intersection). Single value
 * -> equals / is_null; many -> IN (JSON array, comma-safe). Mixed null+values uses the admitted
 * in_or_null operator so Unset remains in the same parenthesized OR group.
 *
 * This is the ONE selection-predicate vocabulary: the widget query, the grid datasource, and
 * spatial focus all go through it so an operator can never drift between them.
 */
export function buildSelectionFilters(entries) {
    const filters = [];
    (entries || []).forEach(entry => {
        if (!entry || !entry.filterColumn) return;
        const values = (entry.values || []).map(v => (v && typeof v === 'object' ? v.value : v));
        if (!values.length) return;
        if (values.length === 1) {
            filters.push(values[0] == null
                ? { column: entry.filterColumn, operator: 'is_null' }
                : { column: entry.filterColumn, operator: 'equals', value: values[0] });
            return;
        }
        const nonNull = [...new Set(values.filter(v => v != null))];
        if (nonNull.length) {
            filters.push({
                column: entry.filterColumn,
                operator: values.some(v => v == null) ? 'in_or_null' : 'in',
                value: JSON.stringify(nonNull),
            });
        } else {
            filters.push({ column: entry.filterColumn, operator: 'is_null' });
        }
    });
    return filters;
}

/** Maps one selection entry to the api LegendFilterDto (per-dimension session scope). The dto's
 *  Filters binds the WidgetFilters OBJECT ({ filters: [...] }), not the bare array — a bare array
 *  400s at model binding. The dimension keeps its ORIGINAL table spelling: normalization is a
 *  client-side identity rule, the server receives what the widget declared. */
export function toSessionDimension(entry) {
    const filterList = Array.isArray(entry.filters) ? entry.filters : [];
    return {
        tableName: entry.tableName,
        filterColumn: entry.filterColumn,
        tagColumn: entry.tagColumn,
        filterValues: (entry.values || []).map(v => (v && typeof v === 'object' ? v.value : v)),
        filters: filterList.length ? { filters: filterList } : null,
    };
}

/** Distinct normalized physical tables in selection order. */
function distinctTables(selections) {
    const seen = [];
    (selections || []).forEach(entry => {
        const table = normalizeTableName(entry && entry.tableName);
        if (table && seen.indexOf(table) === -1) seen.push(table);
    });
    return seen;
}

/**
 * The filter-session jobs one selection state needs.
 *
 * - `__full__` carries EVERY dimension: a widget whose table has no active dimension is cross-
 *   filtered by the whole board, exactly as before.
 * - One job per distinct physical table, carrying only the dimensions from OTHER tables. Its
 *   same-table dimensions travel as raw predicates instead (`selectionFiltersForWidget`).
 * - A table with no external dimension would create a duplicate global-only session, so it
 *   reuses the base SID.
 *
 * The existing session cap applies to distinct selected TABLES in selection order (each extra
 * table costs one more session per interaction). Tables past the cap are returned in
 * `frozenTables`: their widgets keep their current data and show the reference badge.
 *
 * @param {Array} selections ordered chartSelections entries
 * @param {number} cap maximum number of table sessions
 * @returns {{ jobs: Array, tables: string[], frozenTables: string[] }}
 */
export function buildTableSessionJobs(selections, cap) {
    const entries = (selections || []).filter(Boolean);
    const tables = distinctTables(entries);
    const limit = Number.isFinite(cap) && cap >= 0 ? cap : tables.length;

    const jobs = [{ target: '__full__', dims: entries.map(toSessionDimension) }];
    tables.slice(0, limit).forEach(table => {
        const dims = entries
            .filter(entry => normalizeTableName(entry.tableName) !== table)
            .map(toSessionDimension);
        jobs.push(dims.length === 0
            ? { target: table, tableName: table, reuseBase: true }
            : { target: table, tableName: table, dims });
    });

    return { jobs, tables, frozenTables: tables.slice(limit) };
}

/** True when the table carries at least one active dimension. */
export function isSelectedTable(selections, tableName) {
    const table = normalizeTableName(tableName);
    if (!table) return false;
    return (selections || []).some(entry => entry && normalizeTableName(entry.tableName) === table);
}

/** True for a table beyond the session cap: its widgets freeze instead of re-querying. A widget
 *  with no table, or on a table nothing selected, is never frozen. */
export function isFrozenTable(selections, tableName, cap) {
    const table = normalizeTableName(tableName);
    if (!table) return false;
    const index = distinctTables(selections).indexOf(table);
    return index >= 0 && Number.isFinite(cap) && index >= cap;
}

/** Same-table dimensions as raw predicates, excluding `excludeWidgetId`'s own dimension. */
function sameTableFilters(selections, tableName, excludeWidgetId) {
    const table = normalizeTableName(tableName);
    if (!table) return [];
    const excluded = excludeWidgetId == null ? null : String(excludeWidgetId);
    return buildSelectionFilters((selections || []).filter(entry => entry
        && normalizeTableName(entry.tableName) === table
        // A source chart must NOT filter itself out of its own bars, or clicking one bar would
        // erase every other bar on that chart.
        && (excluded === null || String(entry.widgetId) !== excluded)));
}

/**
 * The transient raw predicates a widget on `tableName` must add to its own query.
 *
 * A source chart excludes only its OWN dimension (self-excluding). Every other widget on that
 * table — grid, KPI, sibling chart — replays all of them. A widget on an unselected table, or
 * with no physical table at all, gets nothing: the filter session covers it.
 *
 * The source charts' saved/runtime `filters` are deliberately NOT copied. Those scope the source
 * widget's own query; replaying them here would silently narrow a sibling by a filter its author
 * never configured.
 */
export function selectionFiltersForWidget(selections, widgetId, tableName) {
    return sameTableFilters(selections, tableName, widgetId);
}

/** Spatial focus for table T replays EVERY same-table dimension: the viewer paints the exact
 *  cross-filtered row set, so it is never self-excluding. */
export function selectionFiltersForViewer(selections, tableName) {
    return sameTableFilters(selections, tableName, null);
}

// ── exact row window ─────────────────────────────────────────────────────────
// The row window is the ONE place that says how many source rows a scope covers, so the grid, the
// spatial occurrences, and the legend record count can never disagree. It mirrors the server
// `RowWindow` contract (allMatching | topBottom{mode,count,column}).

/** `allMatching` covers every row the admitted predicate matches. */
export function allMatchingRowWindow() {
    return { kind: 'allMatching' };
}

/** A Top/Bottom is only a window when it can actually rank: mode + count + column. An incomplete
 *  one is dropped rather than sent as a half-specified window that fails closed server-side. */
function normalizeTopBottom(topBottom) {
    if (!topBottom || !topBottom.mode || !topBottom.column) return null;
    const match = /^(top|bottom)(\d*)$/i.exec(String(topBottom.mode));
    if (!match) return null;
    const count = Number(topBottom.count != null && topBottom.count !== '' ? topBottom.count : match[2]);
    if (!Number.isFinite(count) || count <= 0) return null;
    return { mode: match[1].toLowerCase(), count, column: topBottom.column };
}

/**
 * A grid displays a ranked slice, so its focus must cover exactly that slice. Runtime Top/Bottom
 * (the widget filter menu) wins over the saved widget default — most specific wins, matching the
 * grid's own datasource precedence.
 */
export function deriveGridRowWindow(runtimeWidgetFilters, sqlViewConfig) {
    const runtime = normalizeTopBottom(runtimeWidgetFilters && runtimeWidgetFilters.topBottom);
    if (runtime) return { kind: 'topBottom', ...runtime };
    const saved = normalizeTopBottom(sqlViewConfig && sqlViewConfig.topBottom);
    if (saved) return { kind: 'topBottom', ...saved };
    return allMatchingRowWindow();
}

/** A chart's Top/Bottom limits GROUPS, not source rows: "top 5 disciplines" still means every row
 *  in those five. The rendered labels carry the exactness instead (`renderedLabelFilter`), so a
 *  chart focus is always all-matching. */
export function deriveChartRowWindow() {
    return allMatchingRowWindow();
}

/** The aggregate remainder bucket a chart renders. It is not a value in the data, so it can never
 *  become a predicate. Mirrors `OTHER_LABEL` in chart-data.service — duplicated as a literal only
 *  because this module stays dependency-free; the harness pins the two together. */
export const CHART_OTHER_LABEL = 'Other';

/** The label a chart renders for a null group. Mirrors `UNSET_LABEL` in chart-data.service; same
 *  duplication rule as above. */
export const CHART_UNSET_LABEL = 'Unset';

/**
 * The predicate for the labels a chart ACTUALLY rendered. The aggregate `Other` bucket is excluded
 * because it maps to no single stored value, so an all-`Other` chart yields no predicate rather
 * than a wrong one.
 *
 * Two accepted input shapes, and the difference matters:
 *   - `{ value, display }` (the `chartSelections` shape): `value` is the SERVER filter value and is
 *     authoritative — `display` is only read to spot the `Other` bucket.
 *   - a plain label (a chart's `labelDomain`): the label IS the value, except for the `Unset`
 *     sentinel, which is a rendering of NULL and is mapped back to null. Without that mapping a
 *     rendered `Unset` slice would become a literal `= 'Unset'` predicate matching no row.
 */
export function renderedLabelFilter(
    filterColumn,
    renderedValues,
    { otherLabel = CHART_OTHER_LABEL, unsetLabel = CHART_UNSET_LABEL } = {},
) {
    const values = [];
    (renderedValues || []).forEach(item => {
        const isObject = item && typeof item === 'object';
        const label = isObject ? (item.display != null ? item.display : item.value) : item;
        if (String(label) === String(otherLabel)) return;
        if (isObject) {
            values.push(item.value);
            return;
        }
        values.push(String(item) === String(unsetLabel) ? null : item);
    });
    return buildSelectionFilters([{ filterColumn, values }]);
}

/**
 * The label an explicit all-matching action shows on a WINDOWED grid, so the user is told the
 * action reaches past the rows on screen. Null when there is no window — the caller keeps its
 * default label, which is already truthful there.
 *
 * `matchingCount` MUST be the pre-window count (`totalFilteredBeforeWindow`). A display route's
 * paging total is counted OVER the window and would announce the window size as if it were the
 * whole match — the exact scope lie this feature removes. When no pre-window count is known the
 * label drops the number rather than inventing one.
 */
export function isolateAllMatchingLabel(rowWindow, matchingCount) {
    if (!rowWindow || rowWindow.kind !== 'topBottom') return null;
    const count = Number(matchingCount);
    if (!Number.isFinite(count) || count <= 0) return 'Isolate all matching';
    return `Isolate all ${count} matching`;
}

// ── spatial focus scopes ─────────────────────────────────────────────────────
// One builder per scope arm, shared by the chart and the grid. The server admits SHAPE before it
// looks at anything else (`SpatialFocusRequestValidator`): a scope that names one kind while
// carrying another arm's fields is a `multipleScopeArms` 400, and a present-but-EMPTY `rowKeys`
// array counts as a supplied arm. Building each arm here — rather than spreading an options object
// at two call sites — makes that failure structurally impossible.

/** Non-empty trimmed string, or null. The one normalization every wire field below goes through. */
function text(value) {
    if (value === null || value === undefined) return null;
    const raw = String(value).trim();
    return raw ? raw : null;
}

/** Trimmed, blank-free, order-preserving unique list, or null when nothing survives. */
function textList(values) {
    const seen = [];
    (Array.isArray(values) ? values : []).forEach(value => {
        const item = text(value);
        if (item && seen.indexOf(item) === -1) seen.push(item);
    });
    return seen.length ? seen : null;
}

/**
 * Whether a view's catalogue `spatial` descriptor can answer an EXACT spatial focus.
 *
 * Three things must hold, and the third is the one that is easy to miss. A direct Register with no
 * designated highlight/context, and a v1 root-grain compiled source, both publish a descriptor with
 * a `rowKey` and a `sourceRevision` but NEITHER role
 * (`SqlViewQueryService.BuildDirectRegisterSpatial` / `BuildSourceSpatial`). The resolver then builds
 * one occurrence per row with no bindings, so the scene renders nothing and CLEARS — a widget that
 * isolates today would go blank. Such a source keeps the pre-existing whole-tag isolate instead.
 *
 * This is a capability decision taken BEFORE the request, never a fallback after a failure: a
 * post-hoc retry would be two live decision paths for one gesture.
 *
 * @param {?object} spatial `TableViewInfo.spatial`
 * @returns {boolean}
 */
export function hasExactSpatialCapability(spatial) {
    if (!spatial) return false;
    if (!text(spatial.rowKey) || !text(spatial.sourceRevision)) return false;
    if (Number(spatial.spatialContractVersion) === 2) {
        return Array.isArray(spatial.bindings)
            && !!spatial.defaultFocus
            && Array.isArray(spatial.defaultFocus.targets)
            && Array.isArray(spatial.defaultFocus.contexts);
    }
    return !!(spatial.target || spatial.context);
}

/**
 * The exact row key a grid row can address itself by, or null when it cannot.
 *
 * `spatial.rowKey` is `Id` for a direct Register and a v1 root-grain source, and `__RowKey` for a v2
 * compiled source. Both are READABLE from a row of a CAPABLE source: the key rides the display
 * payload as a server-owned machine column, added after column admission
 * (`SqlViewQueryService.WithSpatialRowKeyColumn`), so it never joins the author-facing column set
 * and a caller still cannot request or sort by it.
 *
 * Null therefore no longer means "this grain is unaddressable by design" — it means the row in hand
 * does not carry the key (a non-capable source, or a payload from an API build that predates the
 * machine column). Callers must treat that as a SHORTFALL to report, not a silent downgrade; what
 * they must never do is send a guessed key, which the server answers with `unknownRowKey`.
 *
 * @param {?object} rowData a grid row
 * @param {?object} spatial the view's spatial descriptor
 * @returns {?string}
 */
export function spatialRowKeyValue(rowData, spatial) {
    const column = spatial && text(spatial.rowKey);
    if (!rowData || !column) return null;
    return text(rowData[column]);
}

/**
 * The `query` arm: an exact row scope described by filters, search, session and a row window.
 *
 * Admitted fields are exactly `SpatialFocusRequestValidator.QueryFields`. `columns` and `filters`
 * are always present (they are this arm's own fields, and an empty list is a legitimate value);
 * every optional is OMITTED when absent rather than sent as null, because an unknown/foreign field
 * is a typed refusal server-side, not a shrug.
 *
 * `globalFilters` has no place here at all — the server rejects it outright
 * (`globalFiltersNotSupported`), so a caller that still needs it is not expressible as an exact
 * scope and must not pretend otherwise by dropping it.
 *
 * @returns {?object} the scope, or null when it could not be stated exactly
 */
export function buildSpatialQueryScope({
    tableName,
    columns,
    filters,
    search,
    filterSessionId,
    expectedSourceRevision,
    encodingColumn,
    rowWindow,
    registerRoles,
} = {}) {
    const table = text(tableName);
    const revision = text(expectedSourceRevision);
    if (!table || !revision || !rowWindow || !rowWindow.kind) return null;

    const scope = {
        kind: 'query',
        tableName: table,
        columns: textList(columns) || [],
        filters: (Array.isArray(filters) ? filters : []).filter(Boolean),
        rowWindow,
        expectedSourceRevision: revision,
    };
    const searchText = text(search);
    if (searchText) scope.search = searchText;
    const sessionId = text(filterSessionId);
    if (sessionId) scope.filterSessionId = sessionId;
    const encoding = text(encodingColumn);
    if (encoding) scope.encodingColumn = encoding;
    const roles = textList(registerRoles);
    if (roles) scope.registerRoles = roles;
    return scope;
}

/**
 * The `rows` arm: an exact row scope described by keys the caller already holds.
 *
 * Keys are trimmed and deduplicated because the server refuses a repeated key (`duplicateRowKey`)
 * rather than silently collapsing it. No window, no filters, no search: those belong to the other
 * arm, and carrying one of them here is a `multipleScopeArms` 400.
 *
 * @returns {?object} the scope, or null when there is no addressable row
 */
export function buildSpatialRowScope({
    tableName,
    rowKeys,
    expectedSourceRevision,
    encodingColumn,
    registerRoles,
} = {}) {
    const table = text(tableName);
    const revision = text(expectedSourceRevision);
    const keys = textList(rowKeys);
    if (!table || !revision || !keys) return null;

    const scope = {
        kind: 'rows',
        tableName: table,
        rowKeys: keys,
        expectedSourceRevision: revision,
    };
    const encoding = text(encodingColumn);
    if (encoding) scope.encodingColumn = encoding;
    const roles = textList(registerRoles);
    if (roles) scope.registerRoles = roles;
    return scope;
}
